import { EsThread, EsThreadPool } from 'threads-es';
import type { MessageWorkerAPI } from '@/assets/workers/message.worker';
import MessageWorker from '@/assets/workers/message.worker?worker';
import { getActiveUserId } from '@/stores/auth';
import { createLogger } from '@/utils/logger';

type TailParameters<T> = T extends [unknown, ...infer Rest] ? Rest : never;

export default class MessageManager {
  readonly #logger = createLogger('Message');
  #workerPool: EsThreadPool<MessageWorkerAPI> | null = null;

  constructor() {
    MessageManager.#createWorkerPool()
      .then((pool) => {
        this.#workerPool = pool;
      })
      .catch((error) => {
        this.#logger.error(error);
      });
  }

  async dispatch<K extends keyof MessageWorkerAPI>(
    action: K,
    ...input: TailParameters<Parameters<MessageWorkerAPI[K]>>
  ) {
    return MessageManager.#createWorker().then(async (worker) => {
      const activeUserId = getActiveUserId();
      if (!activeUserId) throw 'INVALID_USER_ID';
      if (typeof worker.methods[action] !== 'function') throw `Invalid worker action: ${String(action)}`;

      const params = [activeUserId, ...input] as unknown as Parameters<MessageWorkerAPI[K]>;

      try {
        return await worker.methods[action](...params);
      } catch (error) {
        this.#logger.error(action, params, error);
        throw error;
      } finally {
        await worker.terminate();
      }
    });
  }

  async queue<K extends keyof MessageWorkerAPI>(action: K, ...input: TailParameters<Parameters<MessageWorkerAPI[K]>>) {
    if (!this.#workerPool) {
      this.#workerPool = await MessageManager.#createWorkerPool();
    }

    const activeUserId = getActiveUserId();
    if (!activeUserId) throw 'INVALID_USER_ID';
    const params = [activeUserId, ...input] as unknown as Parameters<MessageWorkerAPI[K]>;

    try {
      return this.#workerPool.queue(async (worker) => {
        if (typeof worker.methods[action] !== 'function') throw `Invalid worker action: ${String(action)}`;

        return await worker.methods[action](...params);
      });
    } catch (error) {
      this.#logger.error(action, params, error);

      throw error;
    }
  }

  static #createWorker() {
    return EsThread.Spawn<MessageWorkerAPI>(new MessageWorker({ name: 'message-worker' }), { timeout: 15_000 });
  }

  static #createWorkerPool() {
    return EsThreadPool.Spawn(
      (id) =>
        EsThread.Spawn<MessageWorkerAPI>(new MessageWorker({ name: `message-worker-${id}` }), { timeout: 15_000 }),
      { size: 3 },
    );
  }
}

export const messageManager = new MessageManager();
